import { useEffect, useState } from "react";
import apiClient from "../../../utils/apiClient";
import { buildBreakdownQuery } from "../formatters";

const emptyTableData = {
  byStatus: [],
  byDaysLate: [],
  byReason: [],
  byServiceType: [],
  byService: [],
  byCarrierAndService: [],
  byFromState: [],
  byToState: [],
  byFromStateAndCompany: [],
  byToStateAndMetroRegional: [],
  top10BestLanes: [],
  top10WorstLanes: [],
  top10KnownReceivers: [],
  top10ReceiversCompletedAndReportable: [],
  top20Senders: [],
  byFyMonth: [],
  byFyQuarter: [],
  top10Surcharges: [],
};

export function useQbrBreakdowns(filters, canLoadData) {
  const [tableData, setTableData] = useState(emptyTableData);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!canLoadData) {
      setTableData(emptyTableData);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    const params = buildBreakdownQuery(filters).toString();

    setError("");
    setTableLoading(true);
    setTableData(emptyTableData);

    apiClient
      .get(`/api/v1/difotVisualisation/breakdowns?${params}`, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.detail || payload.error || "Failed to load DIFOT breakdowns");
        }
        return response.json();
      })
      .then((data) => {
        if (cancelled) return;
        setTableData({
          byStatus: Array.isArray(data.byStatus) ? data.byStatus : [],
          byDaysLate: Array.isArray(data.byDaysLate) ? data.byDaysLate : [],
          byReason: Array.isArray(data.byReason) ? data.byReason : [],
          byServiceType: Array.isArray(data.byServiceType) ? data.byServiceType : [],
          byService: Array.isArray(data.byService) ? data.byService : [],
          byCarrierAndService: Array.isArray(data.byCarrierAndService) ? data.byCarrierAndService : [],
          byFromState: Array.isArray(data.byFromState) ? data.byFromState : [],
          byToState: Array.isArray(data.byToState) ? data.byToState : [],
          byFromStateAndCompany: Array.isArray(data.byFromStateAndCompany) ? data.byFromStateAndCompany : [],
          byToStateAndMetroRegional: Array.isArray(data.byToStateAndMetroRegional)
            ? data.byToStateAndMetroRegional
            : [],
          top10BestLanes: Array.isArray(data.top10BestLanes) ? data.top10BestLanes : [],
          top10WorstLanes: Array.isArray(data.top10WorstLanes) ? data.top10WorstLanes : [],
          top10KnownReceivers: Array.isArray(data.top10KnownReceivers) ? data.top10KnownReceivers : [],
          top10ReceiversCompletedAndReportable: Array.isArray(data.top10ReceiversCompletedAndReportable)
            ? data.top10ReceiversCompletedAndReportable
            : [],
          top20Senders: Array.isArray(data.top20Senders) ? data.top20Senders : [],
          byFyMonth: Array.isArray(data.byFyMonth) ? data.byFyMonth : [],
          byFyQuarter: Array.isArray(data.byFyQuarter) ? data.byFyQuarter : [],
          top10Surcharges: Array.isArray(data.top10Surcharges) ? data.top10Surcharges : [],
        });
      })
      .catch((err) => {
        if (err.name === "AbortError" || cancelled) return;
        setError(err.message || "Failed to load DIFOT breakdowns");
      })
      .finally(() => {
        if (!cancelled) setTableLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [canLoadData, filters]);

  return { tableData, tableLoading, error };
}
